"use client";

import Link from "next/link";
import { ArrowRight, BookOpen } from "lucide-react";
import {
  CrossSellBand,
  DevLandingShell,
  FinalCta,
  GetKeyButton,
  HeroBadge,
  LiveDot,
  SecondaryCta,
  SectionHeading,
  StatsRow,
  StepsCard,
  metalText,
} from "@/components/marketing/dev-landing/chrome";
import { FeatureSplit, TextFeatureList } from "@/components/marketing/dev-landing/sections";
import { FlowDriftMock, GexProfileMock, ScreenerMock } from "@/components/marketing/dev-landing/mocks";
import { C, CodeWindow, K, Line, N, P, S } from "@/components/marketing/dev-landing/code-window";
import { EndpointTable } from "@/components/marketing/dev-landing/endpoint-table";

const q = (v: string) => `"${v}"`;

export default function ApiLandingClient({ keyHref }: { keyHref: string }) {
  return (
    <DevLandingShell>
      {/* Hero */}
      <div className="text-center">
        <HeroBadge>
          <LiveDot />
          <span className="text-sm text-[#d4d5d5]">REST API v1 &middot; live now</span>
        </HeroBadge>

        <h1 className="mx-auto max-w-4xl text-balance text-4xl tracking-tight text-white sm:text-5xl md:text-6xl">
          Real-time <span style={metalText}>options intelligence</span>, as clean JSON
        </h1>

        <p className="mx-auto mt-6 max-w-2xl text-balance text-lg leading-relaxed text-white/70 sm:text-xl">
          Gamma exposure, options flows, screeners and wheel analytics - the same real-time engine
          behind every QuantWheel dashboard, one HTTPS call away.
        </p>

        <div className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <GetKeyButton keyHref={keyHref} eventName="api_lp_get_key_clicked" />
          <SecondaryCta href="/docs">
            <BookOpen className="h-4 w-4" />
            Read the docs
          </SecondaryCta>
        </div>
        <p className="mt-4 text-xs text-white/50 sm:text-sm">Included with every paid QuantWheel plan.</p>
      </div>

      {/* Hero code demo */}
      <div className="relative mx-auto mt-12 max-w-5xl md:mt-16">
        <div
          className="pointer-events-none absolute -inset-12 rounded-full opacity-20"
          style={{
            background: "radial-gradient(ellipse, rgba(16,185,129,0.3) 0%, rgba(154,160,158,0.08) 45%, transparent 70%)",
          }}
          aria-hidden="true"
        />
        <div className="relative grid gap-5 md:grid-cols-2">
          <CodeWindow title="terminal" status="0.04s">
            <Line>
              <P>$ </P>
              <S>curl</S> <N>&quot;https://quantwheel.com/api/v1/gex?symbol=SPY&quot;</N> <P>\</P>
            </Line>
            <Line indent={2}>
              <S>-H</S> <N>&quot;Authorization: Bearer qw_live_...&quot;</N>
            </Line>
          </CodeWindow>
          <CodeWindow title="/api/v1/gex?symbol=SPY" method="GET" status="200 - 38ms">
            <Line>
              <P>{"{"}</P>
            </Line>
            <Line indent={1}>
              <K>{q("symbol")}</K>
              <P>: </P>
              <S>{q("SPY")}</S>
              <P>,</P>
            </Line>
            <Line indent={1}>
              <K>{q("stockPrice")}</K>
              <P>: </P>
              <N>622.41</N>
              <P>,</P>
            </Line>
            <Line indent={1}>
              <K>{q("gammaZone")}</K>
              <P>: </P>
              <S>{q("negative")}</S>
              <P>,</P>
            </Line>
            <Line indent={1}>
              <K>{q("gammaInflection")}</K>
              <P>: </P>
              <N>620.5</N>
              <P>,</P>
            </Line>
            <Line indent={1}>
              <K>{q("callWall")}</K>
              <P>: {"{ "}</P>
              <K>{q("strike")}</K>
              <P>: </P>
              <N>630</N>
              <P>{" }"},</P>
            </Line>
            <Line indent={1}>
              <K>{q("putWall")}</K>
              <P>: {"{ "}</P>
              <K>{q("strike")}</K>
              <P>: </P>
              <N>615</N>
              <P>{" }"},</P>
            </Line>
            <Line indent={1}>
              <K>{q("asOf")}</K>
              <P>: </P>
              <S>{q("2026-08-05T14:32:00Z")}</S> <C>{"// seconds old"}</C>
            </Line>
            <Line>
              <P>{"}"}</P>
            </Line>
          </CodeWindow>
        </div>
      </div>

      <StatsRow
        stats={[
          { value: "20+", label: "REST endpoints" },
          { value: "120", label: "requests / min" },
          { value: "<60s", label: "data freshness" },
          { value: "1", label: "API key" },
        ]}
      />

      {/* What the key unlocks - told as split sections with real visuals */}
      <div className="mt-24 sm:mt-28">
        <SectionHeading
          title={
            <>
              Everything the dashboards know, <span style={metalText}>your code knows</span>
            </>
          }
          sub="No scraping, no delayed feeds, no CSV exports. The API serves the exact numbers QuantWheel renders - computed live from the full options chain."
        />
      </div>

      <FeatureSplit
        eyebrow="Gamma exposure"
        title={
          <>
            Dealer positioning, <span style={metalText}>recomputed on every request</span>
          </>
        }
        lead="Call walls, put walls, the gamma flip and net GEX per strike for any US optionable ticker - plus history, heatmaps and vanna one endpoint away."
        points={[
          { term: "callWall / putWall", text: "the strikes dealers defend, ready to plot" },
          { term: "gammaInflection", text: "the flip between dampened and amplified moves" },
          { term: "/v1/gex/history", text: "replay how walls migrated, for backtests" },
        ]}
        visual={<GexProfileMock />}
      />

      <FeatureSplit
        flip
        eyebrow="Options flows"
        title={
          <>
            The tape, <span style={metalText}>signed and bucketed</span>
          </>
        }
        lead="Aggressor-signed call and put premium per minute, plus the sweeps and blocks big enough to matter. Compact arrays your charts plot directly."
        points={[
          { term: "/v1/flows", text: "net premium drift bars, live or by session" },
          { term: "/v1/flows/trades", text: "significant prints with side, strike and size" },
          { term: "day=YYYY-MM-DD", text: "replay any past session bar by bar" },
        ]}
        visual={<FlowDriftMock />}
      />

      <FeatureSplit
        eyebrow="Screeners & your data"
        title={
          <>
            From market scan to <span style={metalText}>your own wheels</span>
          </>
        }
        lead="Query the same screeners traders use in the app, then pull your wheels, positions and performance to close the loop with automations and reports."
        points={[
          { term: "/v1/screener", text: "options-selling candidates by premium, delta, expiry" },
          { term: "/v1/wheels", text: "your open wheels and stats, programmatically" },
          { term: "/v1/earnings", text: "upcoming earnings to filter risk around" },
        ]}
        visual={<ScreenerMock />}
      />

      {/* The quiet essentials */}
      <div className="mt-20 sm:mt-24">
        <TextFeatureList
          items={[
            {
              title: "Auth that stays out of the way",
              description: (
                <>
                  One key, two headers: <span className="font-mono text-xs text-white/70">Authorization: Bearer</span>{" "}
                  or <span className="font-mono text-xs text-white/70">X-API-Key</span>. Generate and rotate it
                  yourself from the dashboard.
                </>
              ),
            },
            {
              title: "AI-ready by design",
              description: (
                <>
                  Every capability is also exposed as an MCP tool, so Claude, ChatGPT and Cursor can call it in plain
                  English.{" "}
                  <Link href="/mcp" className="text-[#7fe7c8] underline-offset-4 hover:underline">
                    Meet the MCP server
                  </Link>
                  .
                </>
              ),
            },
          ]}
        />
      </div>

      {/* Endpoint catalog */}
      <div className="mt-20 sm:mt-24">
        <SectionHeading
          title="One surface, every signal"
          sub="A quick tour of the catalog. The full request and response reference lives in the docs."
        />
        <EndpointTable
          endpoints={[
            { method: "GET", path: "/v1/gex", description: "Real-time GEX profile for any ticker" },
            { method: "GET", path: "/v1/gex/history", description: "Historical GEX snapshots" },
            { method: "GET", path: "/v1/gex/heatmap", description: "GEX by strike and expiration" },
            { method: "GET", path: "/v1/gex/events", description: "Gamma flip & wall-move events" },
            { method: "GET", path: "/v1/vanna", description: "Vanna & charm exposure" },
            { method: "GET", path: "/v1/flows", description: "Net premium flow bars", tag: "Quant" },
            { method: "GET", path: "/v1/flows/trades", description: "Significant prints - sweeps & blocks", tag: "Quant" },
            { method: "GET", path: "/v1/screener", description: "Options-selling opportunities" },
            { method: "GET", path: "/v1/wheels", description: "Your wheel positions & stats" },
            { method: "GET", path: "/v1/earnings", description: "Upcoming earnings calendar" },
            { method: "GET", path: "/v1/expirations", description: "Available option expirations" },
            { method: "GET", path: "/v1/verify", description: "Validate a key & entitlements" },
          ]}
        />
        <p className="mt-5 text-center text-sm text-white/50">
          <Link href="/docs" className="inline-flex items-center gap-1.5 text-[#7fe7c8] underline-offset-4 hover:underline">
            Browse the full API reference
            <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </p>
      </div>

      {/* How it works */}
      <StepsCard
        title="First call in three minutes"
        steps={[
          {
            title: "Pick a plan",
            description: "Any paid QuantWheel plan includes API access. Flows endpoints ride with Quant.",
          },
          {
            title: "Generate your key",
            description: (
              <>
                Head to{" "}
                <Link href="/dashboard/api" className="text-[#7fe7c8] underline-offset-4 hover:underline">
                  Dashboard &rarr; API
                </Link>{" "}
                and mint a key. Rotate it anytime.
              </>
            ),
          },
          {
            title: "curl and go",
            description: "Hit any endpoint with your Bearer key and get real-time JSON back. That's the setup.",
          },
        ]}
      />

      {/* Go deeper */}
      <div className="mt-20 grid gap-5 sm:mt-24 md:grid-cols-2">
        {[
          {
            href: "/gex-api",
            label: "GEX API",
            title: "Go deeper on gamma exposure",
            description: "Walls, flips, heatmaps, vanna and event streams - the dedicated GEX API tour with live examples.",
          },
          {
            href: "/options-flows-api",
            label: "Options Flows API",
            title: "Go deeper on options flow",
            description: "Premium drift bars and significant prints in real time - the dedicated flows API tour.",
          },
        ].map((card) => (
          <Link
            key={card.href}
            href={card.href}
            className="group rounded-xl border border-[#1f2020] bg-[#101111] p-6 transition-colors hover:border-[#3a3c3b]"
          >
            <span className="text-xs uppercase tracking-wider text-[#8d918f]">{card.label}</span>
            <h3 className="mt-2 flex items-center gap-2 text-lg tracking-tight text-white">
              {card.title}
              <ArrowRight className="h-4 w-4 text-[#9aa09e] transition-transform group-hover:translate-x-0.5" />
            </h3>
            <p className="mt-2 text-sm leading-relaxed text-white/60">{card.description}</p>
          </Link>
        ))}
      </div>

      <CrossSellBand
        badge="Prefer prompts over HTTP?"
        title="The same data, as an MCP server for your AI"
        description="Add QuantWheel to Claude, ChatGPT or Cursor as a connector and ask for walls, flows and setups in plain English - no code required."
        href="/mcp"
        linkLabel="Explore the MCP server"
      />

      <FinalCta
        title="Ship your first call today"
        sub="One key unlocks the entire catalog - real-time GEX, flows, screeners and your own trading analytics."
        keyHref={keyHref}
        eventName="api_lp_get_key_clicked"
        note="Included with every paid QuantWheel plan. Flows endpoints require Quant."
      />
    </DevLandingShell>
  );
}
